/**
 * THE ONE prompt filter for the Prompt Studio (lot B step 2).
 * Pure, no React, no API calls.
 *
 * @see docs/plans/prompt-studio-build/B-popup-frontend.md §Files and props filterModel.ts
 */

import type { Family, LocKey } from './promptStudioModel';
import { listGroups, isClassified } from './promptStudioModel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Filter facet identifiers. */
export type Facet = 'brand' | 'axis' | 'vocab' | 'tags' | 'zone' | 'language' | 'target' | 'status';

/** How to group the prompt list. */
export type GroupBy = 'axis' | 'brand' | 'vocab' | 'tag' | 'zone';

/**
 * THE filter state for the whole popup.
 * Written by the filter panel, a tag click, a Couverture cell drill-in and the
 * coverage pending pill.  Read by the ColumnHeader chips.
 *
 * Facet value conventions:
 *   brand    = brandId
 *   axis     = BrandQueryAxis
 *   vocab    = `${axis}|${term}` | `${axis}|` (aucun) | `${axis}|?` (non classé)
 *   tags     = tag
 *   zone     = LocKey
 *   language = code (a row with no `language` is in no Langue option)
 *   target   (narrative + comparison) = targetId | '' (non détectée) | '?' (non classé)
 *   status   = 'wait' (pending) | 'ok' (classé: isClassified) | '?' (non classé: real axis, no classifiedAt)
 */
export interface FilterState {
  scope: 'brand' | 'tenant';
  facets: Partial<Record<Facet, string[]>>;
  groupBy: GroupBy;
}

/** Empty / default filter state. */
export const EMPTY_FILTER: FilterState = {
  scope: 'brand',
  facets: {},
  groupBy: 'axis',
};

/** Filter used by the coverage pending pill. */
export const PENDING_ONLY: FilterState = {
  ...EMPTY_FILTER,
  facets: { status: ['wait'] },
};

// ---------------------------------------------------------------------------
// termsOf (needed here for vocab facet match)
// ---------------------------------------------------------------------------

import type { BrandQuery, BrandQueryAxis, BrandStrategy } from '@traaker/shared';
import type { Location } from '@traaker/shared';
import { locKeyOf, AXIS_ORDER, MONDIAL } from './promptStudioModel';
import { termsOf, vocabFor } from './coverageModel';
import { effectiveZoneMeta, zoneDisplayLabel } from '@traaker/shared';

/**
 * A `vocab` facet value (`${axis}|${term}`, `${axis}|` = (aucun), `${axis}|?` = non classé)
 * against one row, with Couverture's own termsOf over the axis vocabulary, so a cell's
 * filter matches exactly the rows the cell counted.
 */
/** A row's language, an unlabelled row holding the brand working language (variantModel's rule). */
export function rowLanguage(r: BrandQuery, strategy: BrandStrategy | undefined): string {
  return (r.language ?? strategy?.workingLanguage ?? 'fr').toLowerCase();
}

export function rowVocabKeys(r: BrandQuery, strategy: BrandStrategy | undefined): string[] {
  if (r.axis === 'pending') return [];
  const terms = termsOf(r, vocabFor(strategy, r.axis as BrandQueryAxis));
  if (terms === null) return [`${r.axis}|?`];
  if (terms.length === 0) return [`${r.axis}|`];
  return terms.map((t) => `${r.axis}|${t}`);
}

function vocabMatches(r: BrandQuery, v: string, strategy: BrandStrategy | undefined): boolean {
  return rowVocabKeys(r, strategy).includes(v);
}

/** A vocab key's display: the term; « (aucun) » / « non classé » carry their axis word. */
export function vocabKeyLabel(key: string): string {
  const [ax, ...rest] = key.split('|');
  const term = rest.join('|');
  const axis = AXIS_WORD[ax] ?? ax;
  if (term === '') return `(aucun) · ${axis}`; // i18n: promptStudio.vocab.none
  if (term === '?') return `non classé · ${axis}`; // i18n: promptStudio.vocab.unclassified
  return term;
}

// i18n: promptStudio.axis.*
const AXIS_WORD: Record<string, string> = { narrative: 'Image', authority: 'Autorité', referral: 'Recommandation', comparison: 'Comparaison' };

/** Vocab keys in AXIS_ORDER, then Brand Knowledge order, then (aucun), then non classé. */
export function sortVocabKeys(keys: string[], strategy: BrandStrategy | undefined): string[] {
  const rank = (key: string): [number, number] => {
    const [ax, ...rest] = key.split('|');
    const term = rest.join('|');
    const vocab = vocabFor(strategy, ax as BrandQueryAxis);
    const t = term === '?' ? vocab.length + 2 : term === '' ? vocab.length + 1 : vocab.indexOf(term);
    return [AXIS_ORDER.indexOf(ax as BrandQueryAxis), t < 0 ? vocab.length : t];
  };
  return [...keys].sort((a, b) => {
    const [a1, a2] = rank(a);
    const [b1, b2] = rank(b);
    return a1 - b1 || a2 - b2 || a.localeCompare(b);
  });
}

/** A row's place display (the Lieu facet and the Lieu groups). */
export function placeLabelOf(r: BrandQuery, locations: readonly Location[]): string {
  const z = effectiveZoneMeta(r, locations);
  if (z) return zoneDisplayLabel(z);
  if (locKeyOf(r, locations) === MONDIAL) return 'Mondial'; // i18n: promptStudio.place.mondial
  return r.zone ?? locKeyOf(r, locations);
}

/** Distinct places of a family's active rows: key → label, in row order. */
export function familyPlaceLabels(f: Family, locations: readonly Location[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const r of f.rows) {
    if (r.status !== 'active') continue;
    const k = locKeyOf(r, locations);
    if (!out.has(k)) out.set(k, placeLabelOf(r, locations));
  }
  return out;
}

// ---------------------------------------------------------------------------
// applyFilters
// ---------------------------------------------------------------------------

/**
 * Filter a list of families by free-text query `q` and `FilterState`.
 *
 * - q matches text AND tags, case-insensitive.
 * - vocab + zone + language keep a family when ONE active row matches them all (a Couverture
 *   cell = a row with that term at that place; zone = locKeyOf, the keys covMembers emits;
 *   language = rowLanguage, an unlabelled row holding the working language).
 * - every other facet keeps or drops the whole family.
 */
export function applyFilters(
  fs: Family[],
  q: string,
  f: FilterState,
  locations: readonly Location[] = [],
  strategy?: BrandStrategy,
): Family[] {
  const term = q.trim().toLowerCase();
  const { facets } = f;

  return fs.filter((family) => {
    const { root, rows } = family;

    // Text search — matches text or tags on ANY row
    if (term) {
      const textMatch = rows.some(
        (r) =>
          r.text.toLowerCase().includes(term) ||
          (r.tags ?? []).some((t) => t.toLowerCase().includes(term)),
      );
      if (!textMatch) return false;
    }

    // axis facet — whole-family match on root axis
    if (facets.axis?.length) {
      const ax = root.axis === 'pending' ? undefined : root.axis;
      if (!ax || !facets.axis.includes(ax)) return false;
    }

    // status facet
    if (facets.status?.length) {
      const statusOf = (r: BrandQuery): string => {
        if (r.axis === 'pending') return 'wait';
        if (!r.classifiedAt)     return '?';
        return 'ok';
      };
      if (!rows.some((r) => facets.status!.includes(statusOf(r)))) return false;
    }

    // tags facet — whole-family
    if (facets.tags?.length) {
      const allTags = rows.flatMap((r) => r.tags ?? []);
      if (!facets.tags.some((t) => allTags.includes(t))) return false;
    }

    // vocab + zone + language facets — one active row must match them all
    if (facets.vocab?.length || facets.zone?.length || facets.language?.length) {
      const match = rows.some((r) =>
        r.status === 'active' &&
        (!facets.vocab?.length || facets.vocab.some((v) => vocabMatches(r, v, strategy))) &&
        (!facets.zone?.length || facets.zone.includes(locKeyOf(r, locations))) &&
        (!facets.language?.length || facets.language.includes(rowLanguage(r, strategy))),
      );
      if (!match) return false;
    }

    // target facet — whole-family
    if (facets.target?.length) {
      const targetMatch = rows.some((r) => {
        if (!isClassified(r)) return facets.target!.includes('?');
        if (!r.targetId)      return facets.target!.includes('');
        return facets.target!.includes(r.targetId);
      });
      if (!targetMatch) return false;
    }

    // brand facet — whole-family
    if (facets.brand?.length) {
      if (!facets.brand.includes(root.brandId)) return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// facetCounts
// ---------------------------------------------------------------------------

/**
 * Counts for a specific facet, applying every OTHER active facet.
 * The own facet is ignored so the panel shows its full option list.
 */
export function facetCounts(
  fs: Family[],
  q: string,
  f: FilterState,
  facet: Facet,
  locations: readonly Location[] = [],
  strategy?: BrandStrategy,
): Record<string, number> {
  const fWithout: FilterState = {
    ...f,
    facets: { ...f.facets, [facet]: undefined },
  };
  const filtered = applyFilters(fs, q, fWithout, locations, strategy);
  const counts: Record<string, number> = {};

  for (const family of filtered) {
    const { rows } = family;
    const active = rows.filter((r) => r.status === 'active');

    switch (facet) {
      case 'axis': {
        const ax = family.root.axis;
        if (ax !== 'pending') {
          counts[ax] = (counts[ax] ?? 0) + 1;
        }
        break;
      }
      case 'status': {
        for (const r of rows) {
          const s = r.axis === 'pending' ? 'wait' : (r.classifiedAt ? 'ok' : '?');
          counts[s] = (counts[s] ?? 0) + 1;
        }
        break;
      }
      case 'tags': {
        for (const t of family.rows.flatMap((r) => r.tags ?? [])) {
          counts[t] = (counts[t] ?? 0) + 1;
        }
        break;
      }
      case 'language': {
        for (const r of rows) {
          const l = rowLanguage(r, strategy);
          counts[l] = (counts[l] ?? 0) + 1;
        }
        break;
      }
      case 'target': {
        for (const r of active) {
          if (!isClassified(r)) { counts['?'] = (counts['?'] ?? 0) + 1; continue; }
          const k = r.targetId ?? '';
          counts[k] = (counts[k] ?? 0) + 1;
        }
        break;
      }
      case 'brand': {
        counts[family.root.brandId] = (counts[family.root.brandId] ?? 0) + 1;
        break;
      }
      case 'vocab': {
        for (const k of new Set(active.flatMap((r) => rowVocabKeys(r, strategy)))) counts[k] = (counts[k] ?? 0) + 1;
        break;
      }
      case 'zone': {
        for (const k of familyPlaceLabels(family, locations).keys()) counts[k] = (counts[k] ?? 0) + 1;
        break;
      }
      default:
        break;
    }
  }

  return counts;
}

// ---------------------------------------------------------------------------
// groupRows
// ---------------------------------------------------------------------------

/**
 * Group families by the specified groupBy key.
 *
 * 'axis'  = listGroups order (pending → AXIS_ORDER → disabled → paused)
 * 'tag'   = a family appears under each tag
 * 'vocab' = unclassified under « non classé » after each axis's terms
 *
 * disabled then paused are always last, whatever the groupBy.
 */
export function groupRows(
  fs: Family[],
  groupBy: GroupBy,
  locations: readonly Location[] = [],
  strategy?: BrandStrategy,
): Array<{ key: string; families: Family[]; distinct: number; label?: string; axis?: BrandQueryAxis }> {
  if (groupBy === 'axis') {
    const groups = listGroups(fs);
    return groups.map(({ key, families }) => ({
      key,
      families,
      distinct: families.length,
    }));
  }

  if (groupBy === 'tag') {
    const tagMap = new Map<string, Family[]>();
    for (const f of fs) {
      const tags = [...new Set(f.rows.flatMap((r) => r.tags ?? []))];
      for (const t of tags) {
        const bucket = tagMap.get(t) ?? [];
        bucket.push(f);
        tagMap.set(t, bucket);
      }
    }
    return Array.from(tagMap.entries()).map(([key, families]) => ({
      key,
      families,
      distinct: families.length,
    }));
  }

  if (groupBy === 'vocab' || groupBy === 'zone') {
    // A family under each of its terms / places (like Tag); pending (vocab only), then
    // « Désactivés » and « Mis en pause » keep their listGroups place at the ends.
    const all = listGroups(fs);
    const head = groupBy === 'vocab' ? all.filter((g) => g.key === 'pending') : [];
    const tail = all.filter((g) => g.key === 'disabled' || g.key === 'paused');
    const aside = new Set([...head, ...tail].flatMap((g) => g.families));
    const buckets = new Map<string, Family[]>();
    const labels = new Map<string, string>();
    for (const f of fs) {
      if (aside.has(f)) continue;
      const keys = groupBy === 'vocab'
        ? [...new Set(f.rows.filter((r) => r.status === 'active').flatMap((r) => rowVocabKeys(r, strategy)))]
        : [...familyPlaceLabels(f, locations)].map(([k, label]) => { labels.set(k, label); return k; });
      for (const k of keys) buckets.set(k, [...(buckets.get(k) ?? []), f]);
    }
    const order = groupBy === 'vocab'
      ? sortVocabKeys([...buckets.keys()], strategy)
      : [...buckets.keys()].sort((a, b) => (a === MONDIAL ? -1 : b === MONDIAL ? 1 : (labels.get(a) ?? a).localeCompare(labels.get(b) ?? b, 'fr')));
    const mid = order.map((key) => ({
      key: `${groupBy}:${key}`,
      families: buckets.get(key)!,
      distinct: buckets.get(key)!.length,
      label: groupBy === 'vocab' ? vocabKeyLabel(key) : labels.get(key) ?? key,
      axis: groupBy === 'vocab' ? (key.split('|')[0] as BrandQueryAxis) : undefined,
    }));
    const plain = (gs: typeof all) => gs.filter((g) => g.families.length > 0).map(({ key, families }) => ({ key, families, distinct: families.length }));
    return [...plain(head), ...mid, ...plain(tail)];
  }

  // Default: axis grouping for other groupBy values
  const groups = listGroups(fs);
  return groups.map(({ key, families }) => ({
    key,
    families,
    distinct: families.length,
  }));
}

// ---------------------------------------------------------------------------
// withTag / fromCell / activeChips / withoutChip / matchOption
// ---------------------------------------------------------------------------

import type { CellTriple } from './promptStudioModel';

/**
 * Tag click in a row (mock S.F.tag = [t]):
 * REPLACES facets.tags with [tag], keeps every other facet.
 * The caller also clears openId and q and switches to tab 'prompts'.
 */
export function withTag(f: FilterState, tag: string): FilterState {
  return { ...f, facets: { ...f.facets, tags: [tag] } };
}

/**
 * Filled coverage cell drill-in:
 * { facets: { vocab: [`${ax}|${tp ?? ''}`], zone: members }, scope: 'brand', groupBy: 'axis' }
 *
 * members = covMembers(c.loc) (the cell's rows' locKeyOf, [c.loc] unless the
 * R19 Couverture match merged a column), so the list shows exactly the cell's rows.
 */
export function fromCell(
  c: CellTriple,
  members: LocKey[],
): FilterState {
  const vocabKey = `${c.ax}|${c.tp ?? ''}`;
  return {
    scope: 'brand',
    groupBy: 'axis',
    facets: {
      vocab: [vocabKey],
      zone: members,
    },
  };
}

/** Active filter chips for the column header, in facet order. */
export function activeChips(
  f: FilterState,
): Array<{ facet: Facet; value: string }> {
  const order: Facet[] = ['brand', 'axis', 'vocab', 'tags', 'zone', 'language', 'target', 'status'];
  const chips: Array<{ facet: Facet; value: string }> = [];
  for (const facet of order) {
    for (const value of f.facets[facet] ?? []) {
      chips.push({ facet, value });
    }
  }
  return chips;
}

/** Remove one chip from the filter state. */
export function withoutChip(
  f: FilterState,
  facet: Facet,
  value: string,
): FilterState {
  const current = f.facets[facet] ?? [];
  const next = current.filter((v) => v !== value);
  return {
    ...f,
    facets: {
      ...f.facets,
      [facet]: next.length > 0 ? next : undefined,
    },
  };
}

/**
 * Accent-insensitive substring match for filter option search.
 */
export function matchOption(label: string, query: string): boolean {
  const normalize = (s: string) =>
    s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  return normalize(label).includes(normalize(query));
}
